import {
  SelectionList,
  type SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import type { Table as TanstackTable } from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

import type { DragMoveEvent } from 'utils/useOrderedDragDrop';

import type { ConversationsTableFeatures } from '../../../../conversationsTable.columns';
import { getConversationsTableColumnHeader } from '../../../../conversationsTable.utils';
import type { UseConversationsTableLayoutResult } from '../../../../utils/useConversationsTableLayout';

import { ConversationsTablePreferencesColumnsItem } from './components/item/conversationsTablePreferencesColumnsItem';

function trackIsColumnPinnedChange(columnId: string, isPinned: boolean) {
  if (isPinned) {
    ampli.conversationsTableColumnPinned({ column: columnId });
  } else {
    ampli.conversationsTableColumnUnpinned({ column: columnId });
  }
}

export type ConversationsTablePreferencesColumnsProps = {
  /** The table whose columns the user chooses from. */
  table: TanstackTable<ConversationsTableFeatures, SearchConversationEnriched>;
  /** The user's saved layout of the table. */
  layout: UseConversationsTableLayoutResult;
};

/** A list that lets the user choose and reorder the columns of the conversations table. */
export const ConversationsTablePreferencesColumns = ({
  table,
  layout,
}: ConversationsTablePreferencesColumnsProps): JSX.Element => {
  const { setIsColumnVisible, setIsColumnPinned, setColumnOrder } = layout;

  // Lists the columns in the order the table shows them, with the pinned ones first.
  const columns = [
    ...table.getStartLeafColumns(),
    ...table.getCenterLeafColumns(),
  ].flatMap((column) => {
    const label = getConversationsTableColumnHeader(column);
    return label ? [{ column, label }] : [];
  });

  const visibleColumnIds = columns
    .filter(({ column }) => column.getIsVisible())
    .map(({ column }) => column.id);

  const handleChange = useCallback(
    ({ selectedItem }: SelectionListChangeEvent<string>) => {
      setIsColumnVisible(selectedItem.value, selectedItem.isSelected);

      if (selectedItem.isSelected) {
        ampli.conversationsTableColumnShown({ column: selectedItem.value });
      } else {
        ampli.conversationsTableColumnHidden({ column: selectedItem.value });
      }
    },
    [setIsColumnVisible],
  );

  const handleIsPinnedChange = (columnId: string, isPinned: boolean) => {
    setIsColumnPinned(columnId, isPinned);
    trackIsColumnPinnedChange(columnId, isPinned);
  };

  const handleMove = ({ dragIndex, hoverIndex }: DragMoveEvent) => {
    const columnIds = columns.map(({ column }) => column.id);
    const [movedColumnId] = columnIds.splice(dragIndex, 1);
    columnIds.splice(hoverIndex, 0, movedColumnId);

    const pinnedCount = columns.filter(
      ({ column }) => column.getIsPinned() === 'start',
    ).length;
    const wasPinned = dragIndex < pinnedCount;
    // A column dropped onto a pinned column is pinned, and one dropped onto an unpinned column is unpinned.
    const isPinned = hoverIndex < pinnedCount;

    setColumnOrder(columnIds, movedColumnId, isPinned);
    ampli.conversationsTableColumnMoved({ column: movedColumnId });

    if (isPinned !== wasPinned) {
      trackIsColumnPinnedChange(movedColumnId, isPinned);
    }
  };

  return (
    <SelectionList
      isMultiple
      selectedValues={visibleColumnIds}
      onChange={handleChange}
      // Takes focus from the density list, so the keyboard only navigates the list under the mouse.
      onMouseEnter={(e) => e.currentTarget.focus({ preventScroll: true })}
    >
      <SelectionList.Section title="Columns">
        {columns.map(({ column, label }, index) => (
          <ConversationsTablePreferencesColumnsItem
            key={column.id}
            column={column}
            label={label}
            index={index}
            onMove={handleMove}
            onIsPinnedChange={(isPinned) =>
              handleIsPinnedChange(column.id, isPinned)
            }
          />
        ))}
      </SelectionList.Section>
    </SelectionList>
  );
};
